import styled from '@emotion/styled';
import {
  Card,
  CardActions as _CardActions,
  CardContent,
  FormControl,
  Input,
  MenuItem,
  Radio,
  Select,
  Typography,
} from '@material-ui/core';
import { createElement, FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import theme from '../../../../theme';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import mq from '../../../styleUtility/mediaquery';
import { InputtingCreativeFormat } from '../../../types/InputtingCreativeFormat';
import _CreativeFormatCreativeTypeIcon from '../CreativeFormatCreativeTypeIcon';
import _CreativeFormatDevice from '../CreativeFormatDevice';
import CreativeFormatNetworkIcon from '../CreativeFormatNetworkIcon';
import CreativeFormatPlacementLabel from '../CreativeFormatPlacementLabel';

type Props = {
  onClick: () => void;
  creativeFormatIndex: number;
  creativeFormat: InputtingCreativeFormat;
  onChangePlaybackTime: ({ index, secondsList }: { index: number; secondsList: number[] }) => void;
  originalCreativeFormatPlaybackTime: number | null;
  onChange: (e: any) => void;
};

const CreativeFormatItem: FC<Props> = ({
  onClick,
  creativeFormatIndex,
  creativeFormat,
  onChangePlaybackTime,
  originalCreativeFormatPlaybackTime,
  onChange,
}) => {
  const selectedPlaybackTimes = useMemo(
    () =>
      originalCreativeFormatPlaybackTime
        ? originalCreativeFormatPlaybackTime
        : creativeFormat.selectedPlaybackTimes.map((playbackTime) => playbackTime.seconds)[0],
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creativeFormat.selectedPlaybackTimes],
  );

  return (
    <Root onClick={onClick}>
      <CreativeFormatCard variant={'elevation'} selected={!!creativeFormat.selected}>
        <CardContent>
          <CreativeFormatCreativeTypeIcon creativeType={creativeFormat.creativeType} />

          <Device>
            <CreativeFormatDevice
              network={creativeFormat.network}
              aspectRatio={creativeFormat.aspectRatioItem ? creativeFormat.aspectRatioItem.aspectRatio : null}
            />
          </Device>

          <NetworkAndPlacement>
            <NetworkIcon network={creativeFormat.network} size={16} />
            <PlacementWrap>
              <Typography>
                <CreativeFormatPlacementLabel placement={creativeFormat.placement} />
              </Typography>
            </PlacementWrap>
          </NetworkAndPlacement>

          {creativeFormat.aspectRatioItem &&
          creativeFormat.aspectRatioItem.aspectRatio.width * creativeFormat.aspectRatioItem.aspectRatio.height > 0 ? (
            <Typography variant="body2">
              {creativeFormat.aspectRatioItem.aspectRatio.width}:{creativeFormat.aspectRatioItem.aspectRatio.height}（
              {creativeFormat.aspectRatioItem.presetSize.width}:{creativeFormat.aspectRatioItem.presetSize.height}）
            </Typography>
          ) : (
            <Typography variant="body2">
              <FormattedMessage id="creative_format.aspect_ratio.nullable" />
            </Typography>
          )}
        </CardContent>

        {creativeFormat.playbackTime && creativeFormat.playbackTime.availables.length > 0 && (
          <CardActions>
            <FormControl>
              <Select
                disabled
                input={<Input name="select-playback-time" />}
                value={selectedPlaybackTimes}
                onChange={(e) => {
                  onChangePlaybackTime({
                    index: creativeFormatIndex,
                    secondsList: [Number(e.target.value)],
                  });
                }}
              >
                {creativeFormat.playbackTime.availables.map(({ seconds }) => (
                  <MenuItem key={`${creativeFormatIndex}-${seconds}`} value={seconds}>
                    <FormattedMessage id="creative_format.playback_time" values={{ seconds }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardActions>
        )}
      </CreativeFormatCard>
      <Radio checked={creativeFormat.selected} onChange={onChange} value={creativeFormat.originalArrayKey} />
    </Root>
  );
};

const Root = styled.div({
  width: '100%',
  height: '100%',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
});

const CreativeFormatCard = styled(Card)(({ selected }: { selected: boolean }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  height: '100%',
  minHeight: '235px',
  textAlign: 'center',
  border: selected ? `2px solid ${theme.palette.primary.main}` : 'default',
  [mq('sp')]: {
    minHeight: '308px',
  },
}));

const PlacementWrap = styled.div({
  maxWidth: '150px',
  wordBreak: 'break-all',
  [mq('sp')]: {
    width: '60px',
  },
});

const CreativeFormatCreativeTypeIcon = styled(_CreativeFormatCreativeTypeIcon)({
  position: 'absolute',
  top: SPACING_UNIT,
  left: SPACING_UNIT,
});

const Device = styled.div({
  height: 120,
  paddingBottom: SPACING_REGULAR,
});

const CreativeFormatDevice = styled(_CreativeFormatDevice)({
  height: '100%',
});

const NetworkAndPlacement = styled('div')(
  flexibleLayoutContainer({
    alignItems: 'center',
    justifyContent: 'center',
  }),
);

const NetworkIcon = styled(CreativeFormatNetworkIcon)({
  marginRight: SPACING_UNIT,
});

const CardActions = styled(_CardActions)({
  marginTop: `-${SPACING_REGULAR}px`,
  justifyContent: 'center',
  [mq('sp')]: {
    marginTop: '8px',
  },
});

export default CreativeFormatItem;
